import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { useActivity } from "@/context/ActivityContext";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { InlineError } from "@/components/InlineError";
import Pagination from "@/components/Pagination";
import { Warehouse, Store, Plus, Pencil, Loader2 } from "lucide-react";

export default function Locations() {
  const { activities, current } = useActivity();
  const acts = activities.filter((a) => a.status !== "archived");
  const [locs, setLocs] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", activity_ids: [] });
  const [edit, setEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");
  const [confirmSave, setConfirmSave] = useState(false);
  const [editErr, setEditErr] = useState("");
  const [page, setPage] = useState(1);

  const load = async () => {
    if (!current?.id) { setLocs([]); return; }
    const { data } = await api.get(`/locations?activity_id=${current.id}`);
    // Garde-fou côté client : ne jamais afficher un emplacement d'une autre activité
    setLocs((data || []).filter((l) => (l.activity_id ? l.activity_id === current.id : (l.activity_ids || []).includes(current.id))));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [current?.id]);

  const actName = (id) => acts.find((a) => a.id === id)?.name || id;
  const selectAct = (id, obj, set) => set({ ...obj, activity_ids: [id] });

  const askSave = () => {
    setSaveErr("");
    if (!form.name.trim()) { setSaveErr("Le nom du point de vente est obligatoire."); return; }
    if (form.activity_ids.length !== 1) { setSaveErr("Choisissez l'activité de ce point de vente."); return; }
    setConfirmSave(true);
  };
  const save = async () => {
    setSaving(true); setSaveErr("");
    try { await api.post("/locations", { name: form.name, type: "pos", activity_ids: form.activity_ids }); toast.success("Point de vente créé"); setConfirmSave(false); setOpen(false); setForm({ name: "", activity_ids: [] }); load(); }
    catch (e) { setConfirmSave(false); setSaveErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };
  const saveEdit = async () => {
    setEditErr("");
    if (edit.type === "pos" && (edit.activity_ids || []).length !== 1) { setEditErr("Choisissez l'activité de ce point de vente."); return; }
    setSaving(true);
    try { await api.patch(`/locations/${edit.id}`, { name: edit.name, activity_ids: edit.type === "pos" ? edit.activity_ids : undefined }); toast.success("Emplacement modifié"); setEdit(null); load(); }
    catch (e) { setEditErr(apiErr(e.response?.data?.detail, e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h1 className="text-2xl md:text-3xl font-bold font-head text-[#16324F] tracking-tight">Emplacements</h1>
          <p className="text-sm text-[#64748B] mt-1">Chaque activité a son emplacement principal ; ajoutez des points de vente si besoin. Le stock est suivi par emplacement et par activité.</p></div>
        <button onClick={() => { setSaveErr(""); setOpen(true); }} className="btn-primary" data-testid="new-location-btn"><Plus className="w-4 h-4" /> Nouveau point de vente</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {locs.slice((page - 1) * 10, page * 10).map((l) => (
          <div key={l.id} className="card p-5" data-testid={`location-card-${l.id}`}>
            <div className="flex items-start justify-between">
              <div className={`w-11 h-11 rounded-lg flex items-center justify-center ${l.type === "warehouse" ? "bg-[#16324F]/10 text-[#16324F]" : "bg-[#0E7490]/10 text-[#0E7490]"}`}>
                {l.type === "warehouse" ? <Warehouse className="w-5 h-5" /> : <Store className="w-5 h-5" />}
              </div>
              <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-[#EFF6FF] text-[#2563EB]">{l.type === "warehouse" ? "Entrepôt principal" : (l.is_primary ? "Emplacement principal" : "Point de vente")}</span>
            </div>
            <h3 className="mt-3 font-semibold font-head text-[#16324F]">{l.name}</h3>
            <p className="text-xs text-[#64748B] mt-1">{(l.activity_ids || []).length ? (l.activity_ids || []).map(actName).join(", ") : "Toutes les activités"}</p>
            {!(l.is_primary || l.type === "warehouse") && <button onClick={() => { setEditErr(""); setEdit({ ...l, activity_ids: l.activity_ids || [] }); }} className="btn-edit !py-1.5 !px-3 mt-4" data-testid={`edit-location-${l.id}`}><Pencil className="w-3.5 h-3.5" /> Modifier</button>}
            {(l.is_primary || l.type === "warehouse") && <p className="text-[11px] text-[#94A3B8] mt-4">Emplacement principal de l'activité (non modifiable ici).</p>}
          </div>
        ))}
      </div>
      <Pagination page={page} setPage={setPage} total={locs.length} />

      <Modal open={open} onClose={() => setOpen(false)} title="Nouveau point de vente" testId="location-modal"
        footer={<><button className="btn-outline" onClick={() => setOpen(false)}>Annuler</button><button className="btn-primary" onClick={askSave} disabled={saving} data-testid="save-location-btn">Créer</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom du point de vente</label><input className="inp" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="location-name-input" placeholder="Ex : Boutique Marché Central" /></div>
          <div><label className="lbl">Activité (un point de vente appartient à une seule activité)</label>
            <div className="space-y-2">{acts.map((a) => (
              <label key={a.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] cursor-pointer" data-testid={`location-act-${a.id}`}>
                <span className="text-sm">{a.name}</span>
                <input type="radio" name="loc-activity" className="w-4 h-4 accent-[#2563EB]" checked={form.activity_ids.includes(a.id)} onChange={() => selectAct(a.id, form, setForm)} />
              </label>))}</div>
          </div>
          <InlineError message={saveErr} title="Création impossible" />
        </div>
      </Modal>

      <ConfirmDialog open={confirmSave} danger={false} title="Confirmer la création" message={`Créer le point de vente « ${form.name} » ?`} confirmLabel="Valider et créer" loading={saving} onConfirm={save} onClose={() => setConfirmSave(false)} />

      <Modal open={!!edit} onClose={() => setEdit(null)} title={`Modifier — ${edit?.name || ""}`} testId="location-edit-modal"
        footer={<><button className="btn-outline" onClick={() => setEdit(null)}>Annuler</button><button className="btn-primary" onClick={saveEdit} disabled={saving} data-testid="save-location-edit-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Enregistrer</button></>}>
        <div className="space-y-4">
          <div><label className="lbl">Nom</label><input className="inp" value={edit?.name || ""} onChange={(e) => setEdit({ ...edit, name: e.target.value })} data-testid="location-edit-name-input" /></div>
          {edit?.type === "pos" && <div><label className="lbl">Activité (un point de vente appartient à une seule activité)</label>
            <div className="space-y-2">{acts.map((a) => (
              <label key={a.id} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border border-[#E2E8F0] bg-[#F8FAFC] cursor-pointer">
                <span className="text-sm">{a.name}</span>
                <input type="radio" name="edit-loc-activity" className="w-4 h-4 accent-[#2563EB]" checked={(edit.activity_ids || []).includes(a.id)} onChange={() => selectAct(a.id, edit, setEdit)} />
              </label>))}</div>
          </div>}
          <InlineError message={editErr} title="Modification impossible" />
        </div>
      </Modal>
    </div>
  );
}
